/**
 * Meta Pixel — consent-gated dynamic injection and dispatch.
 *
 * This module owns the complete browser Pixel lifecycle. Callers may request
 * events through `trackMetaPixelEvent`, but only this controller may touch
 * `fbq`. That keeps slow SDK loads, later consent withdrawal, and same-page
 * regrant on one fail-closed state machine.
 */

const META_PIXEL_PROFILES = Object.freeze({
  'adu-lab': Object.freeze({
    projectId: 'adu-lab',
    pixelId: '2174274586370445',
    hosts: new Set(['adulab.app', 'www.adulab.app']),
  }),
  'adu-lab-uat': Object.freeze({
    projectId: 'adu-lab-uat',
    pixelId: '1047607084790277',
    hosts: new Set(['adu-lab-uat.web.app']),
  }),
});
const PIXEL_ACTIVE = '__meta_pixel_loaded__';
const PIXEL_INITIALIZED = '__meta_pixel_initialized__';
const PIXEL_PAGE_VIEWED = '__meta_pixel_page_viewed__';
const PIXEL_LOADING = '__meta_pixel_loading__';
const PIXEL_GENERATION = '__meta_pixel_generation__';
const PIXEL_PENDING_EVENTS = '__meta_pixel_pending_events__';
const PIXEL_SCRIPT_ID = 'meta-pixel-script';
const PIXEL_STUB_MARKER = '__adu_meta_pixel_stub__';

function getRuntimeProjectId() {
  try {
    return String(import.meta.env?.VITE_FIREBASE_PROJECT_ID || '').trim();
  } catch {
    return '';
  }
}

function getActiveMetaPixelProfile() {
  const projectId = getRuntimeProjectId();
  const profile = META_PIXEL_PROFILES[projectId];
  if (!profile) return null;

  try {
    return profile.hosts.has(window.location.hostname) ? profile : null;
  } catch {
    return null;
  }
}

/**
 * Resolve whether a browser host belongs to the exact Pixel profile selected
 * by the current Firebase project build. Known-project/foreign-host and
 * unknown-project combinations fail closed.
 */
export function canTrackMetaPixel(hostname, projectId = getRuntimeProjectId()) {
  try {
    const profile = META_PIXEL_PROFILES[String(projectId || '').trim()];
    const resolvedHostname = hostname || window.location.hostname;
    return !!profile && profile.hosts.has(resolvedHostname);
  } catch {
    return false;
  }
}

function getPendingEvents() {
  if (!Array.isArray(window[PIXEL_PENDING_EVENTS])) {
    window[PIXEL_PENDING_EVENTS] = [];
  }
  return window[PIXEL_PENDING_EVENTS];
}

function clearPendingEvents() {
  if (Array.isArray(window[PIXEL_PENDING_EVENTS])) {
    window[PIXEL_PENDING_EVENTS].length = 0;
  }
}

function isSdkReady() {
  return typeof window.fbq === 'function' && typeof window.fbq.callMethod === 'function';
}

function sendEvent({ eventName, params, options, pixelId }) {
  try {
    if (!pixelId) return;
    if (options) {
      window.fbq('trackSingle', pixelId, eventName, params || {}, options);
      return;
    }
    window.fbq('trackSingle', pixelId, eventName, params || {});
  } catch {
    // A measurement failure must never interrupt product behavior or the
    // remaining consented event queue.
  }
}

function flushPendingEvents() {
  const pending = getPendingEvents().splice(0);
  pending.forEach(sendEvent);
}

function removeOwnedStub() {
  const fbq = window.fbq;
  if (typeof fbq !== 'function' || fbq[PIXEL_STUB_MARKER] !== true) return;
  if (Array.isArray(fbq.queue)) fbq.queue.length = 0;
  if (window._fbq === fbq) delete window._fbq;
  delete window.fbq;
}

function installPixelStub() {
  if (typeof window.fbq === 'function') return;
  const fbq = function () {
    fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
  };
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  fbq[PIXEL_STUB_MARKER] = true;
}

function activatePixel(generation, profile) {
  if (window[PIXEL_GENERATION] !== generation) return;
  if (typeof window.fbq !== 'function') return;

  const activeProfile = getActiveMetaPixelProfile();
  if (!activeProfile || activeProfile !== profile) {
    window[PIXEL_LOADING] = false;
    clearPendingEvents();
    return;
  }

  if (
    window[PIXEL_INITIALIZED] &&
    window[PIXEL_INITIALIZED] !== profile.pixelId
  ) {
    window[PIXEL_LOADING] = false;
    clearPendingEvents();
    try {
      window.fbq('consent', 'revoke');
    } catch {
      // Local denial remains authoritative if the vendor call fails.
    }
    return;
  }

  try {
    // Consent is explicit before initialization or any event dispatch. This is
    // also what resumes a resident SDK after an earlier revoke.
    window.fbq('consent', 'grant');
    if (!window[PIXEL_INITIALIZED]) {
      window.fbq('init', profile.pixelId);
      window[PIXEL_INITIALIZED] = profile.pixelId;
    }

    window[PIXEL_ACTIVE] = true;
    window[PIXEL_LOADING] = false;

    if (window[PIXEL_PAGE_VIEWED] !== profile.pixelId) {
      window.fbq('trackSingle', profile.pixelId, 'PageView');
      window[PIXEL_PAGE_VIEWED] = profile.pixelId;
    }

    flushPendingEvents();
  } catch (error) {
    window[PIXEL_ACTIVE] = false;
    window[PIXEL_LOADING] = false;
    clearPendingEvents();
    if (isSdkReady()) {
      try {
        window.fbq('consent', 'revoke');
      } catch {
        // The controller remains locally denied even if the vendor call fails.
      }
    }
    console.warn('[consent/metaPixelTag] init failed:', error);
  }
}

/**
 * Start or resume Meta Pixel after an effective marketing grant.
 * Idempotent while active and single-flight while the SDK is loading.
 */
export function loadMetaPixel() {
  const profile = getActiveMetaPixelProfile();
  if (!profile) return;
  if (window[PIXEL_ACTIVE]) return;

  if (isSdkReady()) {
    const generation = (window[PIXEL_GENERATION] || 0) + 1;
    window[PIXEL_GENERATION] = generation;
    activatePixel(generation, profile);
    return;
  }

  if (window[PIXEL_LOADING] || document.getElementById(PIXEL_SCRIPT_ID)) return;

  const generation = (window[PIXEL_GENERATION] || 0) + 1;
  window[PIXEL_GENERATION] = generation;
  window[PIXEL_LOADING] = true;
  installPixelStub();

  const script = document.createElement('script');
  script.id = PIXEL_SCRIPT_ID;
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  script.onload = () => activatePixel(generation, profile);
  script.onerror = () => {
    if (window[PIXEL_GENERATION] !== generation) return;
    window[PIXEL_GENERATION] = generation + 1;
    window[PIXEL_LOADING] = false;
    clearPendingEvents();
    script.remove();
    removeOwnedStub();
  };
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode.insertBefore(script, firstScript);
}

/**
 * Queue a consented application event behind Pixel initialization, or dispatch
 * it immediately when the controller is active. Events are never written into
 * Meta's raw pre-SDK queue ahead of `init`.
 */
export function trackMetaPixelEvent(eventName, params, options) {
  try {
    const profile = getActiveMetaPixelProfile();
    if (!profile) return;
    const event = {
      eventName,
      params: params || {},
      options,
      pixelId: profile.pixelId,
    };

    if (window[PIXEL_ACTIVE] && typeof window.fbq === 'function') {
      sendEvent(event);
      return;
    }

    if (window[PIXEL_LOADING]) {
      getPendingEvents().push(event);
    }
  } catch {
    // Never block the app for tracking failures.
  }
}

/**
 * Synchronously deny browser Pixel activity after consent withdrawal, DNS, or
 * GPC. Executed SDK code cannot be unloaded, so a resident SDK is explicitly
 * revoked and may only resume through a later `loadMetaPixel` grant.
 */
export function cleanupMetaPixel() {
  try {
    window[PIXEL_GENERATION] = (window[PIXEL_GENERATION] || 0) + 1;
    window[PIXEL_ACTIVE] = false;
    window[PIXEL_LOADING] = false;
    clearPendingEvents();

    const pendingScript = document.getElementById(PIXEL_SCRIPT_ID);
    pendingScript?.removeAttribute('src');
    pendingScript?.remove();

    if (isSdkReady()) {
      try {
        window.fbq('consent', 'revoke');
      } catch {
        // Local denial and cookie cleanup remain authoritative if the SDK fails.
      }
    } else {
      removeOwnedStub();
    }

    const host = window.location.hostname;
    // _fbp and _fbc are first-party cookies set by fbevents.js.
    document.cookie = `_fbp=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${host}`;
    document.cookie = `_fbc=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${host}`;
    if (!host.startsWith('.')) {
      document.cookie = `_fbp=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${host}`;
      document.cookie = `_fbc=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${host}`;
    }
  } catch (error) {
    console.warn('[consent/metaPixelTag] cleanup failed:', error);
  }
}
